import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  ArrowRight,
  Clock, 
  CheckCircle, 
  ExternalLink,
  Send,
  Video,
  MessageSquare,
  Mic,
  MicOff,
  Upload,
  Pencil,
  Loader2,
  Sparkles,
  X,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline video player for coach critique videos
const CritiqueVideoPlayer = ({ url, label, onClose }) => {
  const isYoutube = url?.includes('youtube') || url?.includes('youtu.be');
  const isVimeo = url?.includes('vimeo');
  
  const getYoutubeId = (u) => u.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1] || '';
  const getVimeoId = (u) => u.match(/vimeo\.com\/(\d+)/)?.[1] || '';

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl overflow-hidden w-full max-w-4xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm truncate">{label}</h3>
          <div className="flex items-center gap-2">
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Open in new tab</a>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="aspect-video bg-black">
          {isYoutube ? (
            <iframe src={`https://www.youtube.com/embed/${getYoutubeId(url)}?autoplay=1`} className="w-full h-full" allowFullScreen allow="autoplay" frameBorder="0" />
          ) : isVimeo ? (
            <iframe src={`https://player.vimeo.com/video/${getVimeoId(url)}?autoplay=1`} className="w-full h-full" allowFullScreen allow="autoplay" frameBorder="0" />
          ) : (
            <video src={url} controls autoPlay className="w-full h-full" />
          )}
        </div>
      </div>
    </div>
  );
};

const CoachDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState({ critiques: [], stats: {} });
  const [completedCritiques, setCompletedCritiques] = useState([]);
  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [selectedCritique, setSelectedCritique] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [coachVideoPlayer, setCoachVideoPlayer] = useState(null);
  const [responseType, setResponseType] = useState('text');
  const [responseContent, setResponseContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [enhancingText, setEnhancingText] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setResponseContent(prev => {
            const newContent = prev ? prev + ' ' + finalTranscript : finalTranscript;
            return newContent;
          });
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Don't stop on 'no-speech' or 'aborted' - these are normal
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;
        }
        shouldRestartRef.current = false;
        setIsListening(false);
      };

      recognition.onend = () => {
        // Auto-restart if we should still be listening
        if (shouldRestartRef.current) {
          try {
            recognition.start();
          } catch (err) {
            console.error('Failed to restart speech recognition:', err);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        shouldRestartRef.current = false;
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    fetchQueue();
    fetchCompleted();
  }, []);

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/queue`);
      setQueue(response.data);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompleted = async () => {
    setLoadingCompleted(true);
    try {
      const response = await axios.get(`${API_URL}/api/coach/completed`);
      setCompletedCritiques(response.data.critiques || []);
    } catch (error) {
      console.error('Failed to fetch completed:', error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  const handleRespond = async () => {
    if (!responseContent.trim()) return;

    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/coach/critiques/${selectedCritique.id}/update`, {
          response_type: responseType,
          response_content: responseContent
        });
        alert('Response updated successfully!');
        fetchCompleted();
      } else {
        const response = await axios.post(`${API_URL}/api/coach/critiques/${selectedCritique.id}/respond`, {
          critique_id: selectedCritique.id,
          response_type: responseType,
          response_content: responseContent
        });
        const creditsEarned = response.data.credits_earned || 0;
        if (creditsEarned > 0) {
          alert(`Response submitted! You earned ${creditsEarned} Elevated Credits.`);
        }
        fetchQueue();
        fetchCompleted();
      }
      
      setSelectedCritique(null);
      setIsEditing(false);
      setResponseType('text');
      setResponseContent('');
    } catch (error) {
      console.error('Failed to submit response:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mov'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid video file (MP4, MOV, AVI, or WebM)');
      return;
    }

    // Validate file size (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      alert('Video must be less than 500MB');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/video?folder=responses`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setUploadProgress(progress);
        }
      });

      // Set the uploaded video URL as the response content
      setResponseContent(response.data.url);
      setResponseType('video_upload');
    } catch (error) {
      console.error('Failed to upload video:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      shouldRestartRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      shouldRestartRef.current = true;
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
            setIsListening(true);
          } catch (e) {
            console.error('Failed to restart:', e);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        }, 100);
      }
    }
  };

  const enhanceTextWithAI = async () => {
    if (!responseContent.trim() || responseContent.length < 5) {
      alert('Please write at least a few words before enhancing.');
      return;
    }

    setEnhancingText(true);

    try {
      console.log('Enhancing text:', responseContent);
      const response = await axios.post(`${API_URL}/api/ai/enhance-coach-response`, {
        note: responseContent,
        discipline: selectedCritique?.discipline || 'roping'
      });
      console.log('Enhancement response:', response.data);
      if (response.data.enhanced_note) {
        setResponseContent(response.data.enhanced_note);
      } else {
        alert('No enhanced text returned. Please try again.');
      }
    } catch (error) {
      console.error('Failed to enhance text:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to enhance text: ${errorMsg}`);
    } finally {
      setEnhancingText(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const openEditCritique = (critique) => {
    setSelectedCritique(critique);
    setIsEditing(true);
    if (critique.response) {
      setResponseType(critique.response.type || 'text');
      setResponseContent(critique.response.content || '');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Coach Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">Manage critique requests from your students.</p>
        </div>

        {/* Earnings Card */}
        <Card className="mb-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-1">Your Earnings</h3>
                <p className="text-sm text-muted-foreground">You earn 25% of the Elevated Credits used by students</p>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="font-display font-bold text-3xl text-primary">
                    {queue.stats?.total_earned || 0}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Total Earned</p>
                </div>
                <div className="text-center">
                  <p className="font-display font-bold text-3xl text-secondary">
                    {queue.stats?.completed_count || 0}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Critiques Done</p>
                </div>
                {queue.stats?.potential_earnings > 0 && (
                  <div className="text-center">
                    <p className="font-display font-bold text-3xl text-amber-600">
                      +{queue.stats?.potential_earnings || 0}
                    </p>
                    <p className="text-xs text-muted-foreground uppercase">Pending Earnings</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Film Technique Button */}
        <Card className="mb-6 border-dashed border-2 hover:border-primary/50 transition-colors">
          <Link to="/film-technique">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide">Film Technique</h3>
                  <p className="text-sm text-muted-foreground">Record, edit with slow-mo, add voice-over, save to library</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Link>
        </Card>

        {/* Package Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-3xl text-foreground">
                {queue.stats?.total_pending || 0}
              </p>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Total Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-3xl text-primary">
                {queue.stats?.tuneup_count || 0}
              </p>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Tune-Up</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-3xl text-secondary">
                {queue.stats?.shortgo_count || 0}
              </p>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Short Go</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="font-display font-bold text-3xl text-amber-600">
                {queue.stats?.elevated_count || 0}
              </p>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Elevated</p>
            </CardContent>
          </Card>
        </div>

        {/* Phone Number */}
        {!user?.phone && (
          <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-amber-700 mb-3 font-medium">Add your phone number to receive SMS notifications when athletes submit critiques.</p>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  id="coach-phone"
                  className="flex-1"
                  data-testid="coach-phone-input"
                />
                <Button
                  size="sm"
                  className="rounded-full"
                  onClick={async () => {
                    const phoneVal = document.getElementById('coach-phone').value;
                    if (!phoneVal) return;
                    try {
                      await axios.put(`${API_URL}/api/profile`, { phone: phoneVal });
                      window.location.reload();
                    } catch { alert('Failed to save phone number'); }
                  }}
                  data-testid="save-coach-phone-btn"
                >
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Queue & Completed Tabs */}
        <Card>
          <CardHeader>
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'queue' ? 'default' : 'outline'}
                onClick={() => setActiveTab('queue')}
                className="rounded-full"
                data-testid="queue-tab-btn"
              >
                Critique Queue {queue.critiques?.length > 0 && `(${queue.critiques.length})`}
              </Button>
              <Button
                variant={activeTab === 'completed' ? 'default' : 'outline'}
                onClick={() => setActiveTab('completed')}
                className="rounded-full"
                data-testid="completed-tab-btn"
              >
                Completed {completedCritiques.length > 0 && `(${completedCritiques.length})`}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activeTab === 'queue' ? (
              <>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="spinner" />
                  </div>
                ) : queue.critiques?.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
                    <p className="font-semibold">All caught up!</p>
                    <p className="text-sm text-muted-foreground">No pending critique requests.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {queue.critiques.map(critique => {
                      const packageInfo = {
                        tuneup: { name: 'Rodeo Tune-Up', color: 'bg-primary/10 text-primary border-primary/20', credits: 15 },
                        shortgo: { name: 'Short Go', color: 'bg-secondary/10 text-secondary border-secondary/20', credits: 40 },
                        elevated: { name: 'Elevated', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', credits: 100 }
                      }[critique.critique_package] || { name: 'Standard', color: 'bg-muted text-muted-foreground', credits: critique.credits_used || 1 };
                      
                      const creditsUsed = critique.credits_used || packageInfo.credits;
                      const potentialEarnings = Math.floor(creditsUsed * 0.25);
                      
                      return (
                      <div
                        key={critique.id}
                        className="p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={packageInfo.color}>{packageInfo.name}</Badge>
                              <Badge variant="outline">{critique.discipline}</Badge>
                              {potentialEarnings > 0 && (
                                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                                  Earn +{potentialEarnings}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold">{critique.user_name}</span>
                              {critique.user_region && (
                                <span className="text-sm text-muted-foreground">
                                  — {critique.user_region}
                                </span>
                              )}
                            </div>
                            {critique.note && (
                              <p className="text-muted-foreground text-sm mb-2 line-clamp-2">
                                "{critique.note}"
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDate(critique.created_at)}
                              </span>
                              {critique.video_link ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setCoachVideoPlayer({ url: critique.video_link, label: `${critique.user_name} - Video 1` }); }}
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  <Video className="w-4 h-4" />
                                  Video 1
                                </button>
                              ) : (
                                <span className="text-muted-foreground italic">
                                  Video shared directly
                                </span>
                              )}
                              {critique.additional_videos?.map((v, i) => (
                                <button
                                  key={i}
                                  onClick={(e) => { e.stopPropagation(); setCoachVideoPlayer({ url: v, label: `${critique.user_name} - Video ${i + 2}` }); }}
                                  className="text-primary hover:underline flex items-center gap-1"
                                >
                                  <Video className="w-4 h-4" />
                                  Video {i + 2}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Button
                            onClick={() => { setIsEditing(false); setSelectedCritique(critique); }}
                            className="rounded-full flex-shrink-0"
                            data-testid={`respond-btn-${critique.id}`}
                          >
                            Respond
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Completed Critiques */
              <>
                {loadingCompleted ? (
                  <div className="flex justify-center py-12"><div className="spinner" /></div>
                ) : completedCritiques.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="font-semibold">No completed critiques yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {completedCritiques.map(critique => {
                      const packageInfo = {
                        tuneup: { name: 'Rodeo Tune-Up', color: 'bg-primary/10 text-primary border-primary/20' },
                        shortgo: { name: 'Short Go', color: 'bg-secondary/10 text-secondary border-secondary/20' },
                        elevated: { name: 'Elevated', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' }
                      }[critique.critique_package] || { name: 'Standard', color: 'bg-muted text-muted-foreground' };
                      
                      return (
                        <div key={critique.id} className="p-4 border rounded-lg" data-testid={`completed-critique-${critique.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge className={packageInfo.color}>{packageInfo.name}</Badge>
                                <Badge variant="outline">{critique.discipline}</Badge>
                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Done
                                </Badge>
                                {critique.response?.updated_at && (
                                  <Badge variant="outline" className="text-xs">Edited</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold">{critique.user_name}</span>
                                {critique.user_region && (
                                  <span className="text-sm text-muted-foreground">— {critique.user_region}</span>
                                )}
                              </div>
                              {critique.note && (
                                <p className="text-muted-foreground text-sm mb-2 line-clamp-2">"{critique.note}"</p>
                              )}
                              {/* Show the response preview */}
                              {critique.response && (
                                <div className="bg-muted/30 p-3 rounded-lg mt-2 mb-2">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Your Response ({critique.response.type})</p>
                                  {critique.response.type === 'text' ? (
                                    <p className="text-sm line-clamp-3">{critique.response.content}</p>
                                  ) : (
                                    <button
                                      onClick={() => setCoachVideoPlayer({ url: critique.response.content, label: `Response to ${critique.user_name}` })}
                                      className="text-primary text-sm hover:underline inline-flex items-center gap-1"
                                    >
                                      <Play className="w-3 h-3" /> Watch Response
                                    </button>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatDate(critique.completed_at || critique.created_at)}
                                </span>
                                {critique.video_link && (
                                  <button
                                    onClick={() => setCoachVideoPlayer({ url: critique.video_link, label: `${critique.user_name} - Video` })}
                                    className="text-primary hover:underline flex items-center gap-1"
                                  >
                                    <Video className="w-4 h-4" /> Student Video
                                  </button>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => openEditCritique(critique)}
                              className="rounded-full flex-shrink-0"
                              data-testid={`edit-btn-${critique.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Response Dialog */}
        <Dialog open={!!selectedCritique} onOpenChange={() => { setSelectedCritique(null); setIsEditing(false); setResponseType('text'); setResponseContent(''); }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl uppercase">
                {isEditing ? 'Edit Response' : 'Respond to Critique'}
              </DialogTitle>
              <DialogDescription>
                {selectedCritique?.user_name} — {selectedCritique?.discipline}
              </DialogDescription>
            </DialogHeader>

            {selectedCritique && (
              <div className="space-y-6">
                {/* Original Request */}
                <div className="bg-muted/50 p-4 rounded-lg overflow-hidden">
                  <div className="space-y-2">
                    {selectedCritique.video_link && (
                      <button
                        onClick={() => setCoachVideoPlayer({ url: selectedCritique.video_link, label: `${selectedCritique.user_name} - Video 1` })}
                        className="text-primary hover:underline flex items-center gap-2 text-sm"
                      >
                        <Play className="w-4 h-4 flex-shrink-0" />
                        <span>Watch Video 1</span>
                      </button>
                    )}
                    {selectedCritique.additional_videos?.map((v, i) => (
                      <button
                        key={i}
                        onClick={() => setCoachVideoPlayer({ url: v, label: `${selectedCritique.user_name} - Video ${i + 2}` })}
                        className="text-primary hover:underline flex items-center gap-2 text-sm"
                      >
                        <Play className="w-4 h-4 flex-shrink-0" />
                        <span>Watch Video {i + 2}</span>
                      </button>
                    ))}
                  </div>
                  {selectedCritique.note && (
                    <p className="text-muted-foreground break-words mt-3">"{selectedCritique.note}"</p>
                  )}
                </div>

                {/* Response Type */}
                <div className="space-y-3">
                  <Label>Response Type</Label>
                  <RadioGroup value={responseType} onValueChange={(val) => {
                    setResponseType(val);
                    setResponseContent(''); // Clear content when switching types
                  }} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="text" id="type-text" />
                      <Label htmlFor="type-text" className="flex items-center gap-2 cursor-pointer flex-1 text-sm">
                        <MessageSquare className="w-4 h-4 flex-shrink-0" />
                        <span>Text Response</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="video_upload" id="type-video-upload" />
                      <Label htmlFor="type-video-upload" className="flex items-center gap-2 cursor-pointer flex-1 text-sm">
                        <Upload className="w-4 h-4 flex-shrink-0" />
                        <span>Upload Video</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="video_link" id="type-video" />
                      <Label htmlFor="type-video" className="flex items-center gap-2 cursor-pointer flex-1 text-sm">
                        <Video className="w-4 h-4 flex-shrink-0" />
                        <span>Video Link</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="voice_link" id="type-voice" />
                      <Label htmlFor="type-voice" className="flex items-center gap-2 cursor-pointer flex-1 text-sm">
                        <Mic className="w-4 h-4 flex-shrink-0" />
                        <span>Voice Link</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 border-primary/50 bg-primary/5 col-span-2">
                      <RadioGroupItem value="video_studio" id="type-studio" />
                      <Label htmlFor="type-studio" className="flex items-center gap-2 cursor-pointer flex-1 text-sm">
                        <Pencil className="w-4 h-4 flex-shrink-0 text-primary" />
                        <span className="text-primary font-medium">Video Studio</span>
                        <Badge variant="secondary" className="ml-auto text-xs">NEW</Badge>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Response Content */}
                <div className="space-y-2">
                  <Label>
                    {responseType === 'text' ? 'Your Response' : 
                     responseType === 'video_upload' ? 'Video File' :
                     responseType === 'video_studio' ? 'Video Studio' : 'Response Link'}
                  </Label>
                  {responseType === 'video_studio' ? (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg border border-dashed">
                      <div className="text-center">
                        <Pencil className="w-8 h-8 text-primary mx-auto mb-2" />
                        <h4 className="font-semibold">Annotate the Student's Video</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Draw lines, circles, add text and voice-over to explain your feedback
                        </p>
                      </div>
                      <Button 
                        className="w-full"
                        onClick={() => {
                          // If editing and there's an existing video studio project, open it directly
                          if (isEditing && selectedCritique?.annotated_video_id) {
                            navigate(`/video-studio/${selectedCritique.annotated_video_id}`);
                            return;
                          }
                          // Open Video Studio with the student's video
                          const videoUrl = selectedCritique?.video_url || selectedCritique?.video_link;
                          const critiqueId = selectedCritique?.id;
                          const studentId = selectedCritique?.user_id;
                          if (videoUrl) {
                            navigate(`/video-studio/new?video=${encodeURIComponent(videoUrl)}&critique=${critiqueId}&student=${studentId}`);
                          } else {
                            alert('No video URL found for this critique. Please use a different response method.');
                          }
                        }}
                        data-testid="open-video-studio-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Open Video Studio
                      </Button>
                    </div>
                  ) : responseType === 'text' ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <Textarea
                          placeholder="Write your feedback..."
                          value={responseContent}
                          onChange={(e) => setResponseContent(e.target.value)}
                          rows={6}
                          className={isListening ? 'border-primary ring-2 ring-primary/20' : ''}
                          data-testid="response-textarea"
                        />
                        {isListening && (
                          <div className="absolute top-2 right-2 flex items-center gap-2 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            Listening...
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {speechSupported && (
                          <Button
                            type="button"
                            variant={isListening ? "default" : "outline"}
                            size="sm"
                            onClick={toggleListening}
                            className={`gap-2 ${isListening ? 'bg-red-500 hover:bg-red-600' : ''}`}
                            data-testid="voice-input-btn"
                          >
                            {isListening ? (
                              <>
                                <MicOff className="w-4 h-4" />
                                Stop
                              </>
                            ) : (
                              <>
                                <Mic className="w-4 h-4" />
                                Speak
                              </>
                            )}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={enhanceTextWithAI}
                          disabled={enhancingText || !responseContent.trim() || responseContent.length < 5}
                          className="gap-2"
                          data-testid="enhance-response-btn"
                        >
                          {enhancingText ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Enhancing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              AI Enhance
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {speechSupported 
                          ? 'Speak or type your feedback, then click "AI Enhance" to improve clarity'
                          : 'Type your feedback and click "AI Enhance" to improve clarity'
                        }
                      </p>
                    </div>
                  ) : responseType === 'video_upload' ? (
                    <div className="space-y-3">
                      {!responseContent ? (
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                          {uploadingVideo ? (
                            <div className="space-y-3">
                              <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                              <p className="text-sm text-muted-foreground">
                                Uploading... {uploadProgress}%
                              </p>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div 
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{ width: `${uploadProgress}%` }}
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground mb-3">
                                Upload your video response (MP4, MOV, AVI, WebM - max 500MB)
                              </p>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mov,.mp4,.avi,.webm"
                                  onChange={handleVideoUpload}
                                  className="hidden"
                                  data-testid="video-upload-input"
                                />
                                <Button type="button" variant="outline" className="rounded-full" asChild>
                                  <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Select Video
                                  </span>
                                </Button>
                              </label>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Video className="w-5 h-5 text-primary" />
                              <span className="text-sm font-medium">Video uploaded successfully</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResponseContent('')}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              Remove
                            </Button>
                          </div>
                          <a 
                            href={responseContent} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-primary mt-2 block break-all"
                          >
                            {responseContent}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Input
                      type="url"
                      placeholder="Paste your video or voice recording link..."
                      value={responseContent}
                      onChange={(e) => setResponseContent(e.target.value)}
                      className="break-all"
                      data-testid="response-link-input"
                    />
                  )}
                </div>

                {/* Submit */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedCritique(null)}
                    className="rounded-full w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRespond}
                    disabled={submitting || !responseContent.trim()}
                    className="rounded-full w-full sm:w-auto"
                    data-testid="submit-response-btn"
                  >
                    {submitting ? 'Submitting...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {isEditing ? 'Update Response' : 'Send Response'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Video Player Modal */}
        {coachVideoPlayer && (
          <CritiqueVideoPlayer
            url={coachVideoPlayer.url}
            label={coachVideoPlayer.label}
            onClose={() => setCoachVideoPlayer(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CoachDashboardPage;
